import { SupportedLinkPreview } from '../components/molecules/LinkWithPreview/types';

const DEFAULT_LINK_PREVIEW_PATH = '/api/link-previews/v1/resolve';
const SUPPORTED_LINK_PREVIEW_PROVIDERS = new Set<SupportedLinkPreview['provider']>([
  'apple-music',
  'spotify',
  'youtube',
]);
const SUPPORTED_LINK_PREVIEW_RESOURCE_TYPES = new Set<SupportedLinkPreview['resourceType']>([
  'album',
  'artist',
  'episode',
  'music-video',
  'playlist',
  'show',
  'song',
  'track',
  'video',
]);

const sanitizeConfiguredUrl = (rawUrl?: string): string | null => {
  if (typeof rawUrl !== 'string' || rawUrl.trim().length === 0) {
    return null;
  }

  return rawUrl.trim().replace(/\/+$/, '');
};

const stripTrailingApiSegment = (url: string): string => (
  url.toLowerCase().endsWith('/api') ? url.slice(0, -4) : url
);

const resolveLinkPreviewEndpoint = (): string => {
  const explicitEndpoint = sanitizeConfiguredUrl(process.env.REACT_APP_LINK_PREVIEW_URL);

  if (explicitEndpoint) {
    return explicitEndpoint;
  }

  const configuredApiBaseUrl = sanitizeConfiguredUrl(process.env.REACT_APP_API_URL);

  if (configuredApiBaseUrl) {
    return `${stripTrailingApiSegment(configuredApiBaseUrl)}${DEFAULT_LINK_PREVIEW_PATH}`;
  }

  return DEFAULT_LINK_PREVIEW_PATH;
};

export const buildLinkPreviewResolveUrl = (url: string): string => {
  const endpoint = resolveLinkPreviewEndpoint();
  const separator = endpoint.includes('?') ? '&' : '?';
  return `${endpoint}${separator}url=${encodeURIComponent(url)}`;
};

const isRecord = (value: unknown): value is Record<string, unknown> => (
  value !== null && typeof value === 'object' && !Array.isArray(value)
);

const readRequiredString = (payload: Record<string, unknown>, key: string): string | null => {
  const value = payload[key];
  if (typeof value !== 'string') {
    return null;
  }
  const normalized = value.trim();
  return normalized || null;
};

const readString = (payload: Record<string, unknown>, key: string): string | null => {
  const value = payload[key];
  return typeof value === 'string' ? value.trim() : null;
};

const isHttpUrl = (value: string): boolean => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch (error) {
    return false;
  }
};

const parseSupportedLinkPreview = (payload: unknown): SupportedLinkPreview | null => {
  if (!isRecord(payload)) {
    return null;
  }

  const ariaLabel = readRequiredString(payload, 'ariaLabel');
  const artworkUrl = readString(payload, 'artworkUrl');
  const canonicalUrl = readRequiredString(payload, 'canonicalUrl');
  const eyebrow = readRequiredString(payload, 'eyebrow');
  const meta = readRequiredString(payload, 'meta');
  const provider = payload.provider;
  const resourceType = payload.resourceType;
  const subtitle = readRequiredString(payload, 'subtitle');
  const title = readRequiredString(payload, 'title');

  if (
    !ariaLabel
    || artworkUrl === null
    || !canonicalUrl
    || !eyebrow
    || !meta
    || typeof provider !== 'string'
    || !SUPPORTED_LINK_PREVIEW_PROVIDERS.has(provider as SupportedLinkPreview['provider'])
    || typeof resourceType !== 'string'
    || !SUPPORTED_LINK_PREVIEW_RESOURCE_TYPES.has(resourceType as SupportedLinkPreview['resourceType'])
    || !subtitle
    || !title
  ) {
    return null;
  }

  if (!isHttpUrl(canonicalUrl) || (artworkUrl && !isHttpUrl(artworkUrl))) {
    return null;
  }

  return {
    ariaLabel,
    artworkUrl,
    canonicalUrl,
    eyebrow,
    meta,
    provider: provider as SupportedLinkPreview['provider'],
    resourceType: resourceType as SupportedLinkPreview['resourceType'],
    subtitle,
    title,
  };
};

export const fetchLinkPreview = async (
  url: string,
  options: { signal?: AbortSignal } = {},
): Promise<SupportedLinkPreview> => {
  const response = await fetch(
    buildLinkPreviewResolveUrl(url),
    {
      method: 'GET',
      ...(options.signal ? { signal: options.signal } : {}),
    },
  );

  if (!response.ok) {
    throw new Error(`Link preview lookup failed with status ${response.status}`);
  }

  const payload = await response.json();
  const preview = parseSupportedLinkPreview(payload);
  if (!preview) {
    throw new Error('Invalid link preview response');
  }

  return preview;
};
